import type { ReactNode } from "react";
import {
  createViewMode,
  type ViewMode,
  type ViewModeContextValue,
} from "@/hooks/create-view-mode";

export type PlansViewMode = ViewMode;

const { Provider, useViewMode } = createViewMode({
  storageKey: "otus:plans-view-mode",
  providerName: "PlansViewModeProvider",
  hookName: "usePlansViewMode",
});

/**
 * Single source of truth for the plans grid/list preference, shared between
 * the toggle (in the project header — [[project-header.spec]]) and the plans
 * content area ([[plans-view.spec]]). Lives at the project-layout level so
 * toggling in the header re-renders the view; mirrors the
 * `SelectionModeProvider` pattern used in the same layout.
 *
 * The choice is persisted globally in `localStorage` under
 * `otus:plans-view-mode` (default `"grid"`). Storage semantics — SSR-safety,
 * blocked/throwing storage, best-effort writes — are shared with the photos
 * page's equivalent; see `createViewMode`.
 */
export function PlansViewModeProvider({ children }: { children: ReactNode }) {
  return <Provider>{children}</Provider>;
}

/**
 * Grid/list view preference for the plans page. Returns a
 * `[viewMode, setViewMode]` tuple. Must be used within a
 * `PlansViewModeProvider` (mounted at the project layout). See
 * [[plans-view.spec]].
 */
export function usePlansViewMode(): ViewModeContextValue {
  return useViewMode();
}
